import { EventSignatureSummary, EventTypeBackend } from '../../../types/events.types';
import { EVENT_TYPES_WITH_EVENT_LEVEL_SIGNATURE_ONLY } from '../desktop/history/event-table-types/event-table-types.types';
import {
  isDocusignFileLink,
  resolveDisplayedDocusignFileLinks,
  resolveEventAllDocusignFileLinks,
  resolveEventLevelDocusignFileLinks,
} from '../event-form/utils/resolve-linked-signature-file-links.util';

export type DocumentLinksSource = {
  docusignFileLinks?: string[];
  event?: Parameters<typeof resolveEventLevelDocusignFileLinks>[0];
  linkedSignatureFileLink?: string;
  linkedSignatureId?: string;
  signatureStatus?: string;
};

export type PoolPlanEventSource = {
  conversion?: {
    filesLinks?: string[];
    items?: Array<{ docusignFileLinks?: string[]; filesLinks?: string[] }>;
  };
  docusignFileLinks?: string[];
  filesLinks?: string[];
  pool?: { filesLinks?: string[] };
  sharePlan?: { filesLinks?: string[] };
  signatureSummary?: EventSignatureSummary;
  type?: EventTypeBackend;
};

const uniqueLinks = (links: string[]): string[] => Array.from(new Set(links.filter(Boolean)));

export const resolveSafeRecordFileLinks = (event: PoolPlanEventSource): string[] => {
  if (event.type !== EventTypeBackend.SAFE_CONVERSION) return [];

  return event.conversion?.filesLinks ?? [];
};

export const resolvePoolPlanRecordFileLinks = (event: PoolPlanEventSource): string[] => {
  if (event.type === EventTypeBackend.NEW_POOL) {
    return event.pool?.filesLinks ?? [];
  }

  if (event.type === EventTypeBackend.NEW_PLAN) {
    return event.sharePlan?.filesLinks ?? [];
  }

  return [];
};

export const mergeEventAndRecordFileLinks = (event: PoolPlanEventSource): string[] =>
  uniqueLinks([
    ...(event.filesLinks ?? []),
    ...resolvePoolPlanRecordFileLinks(event),
    ...resolveSafeRecordFileLinks(event),
  ]);

export const resolveExerciseTransactionFilesLinks = (
  item: { filesLinks?: string[] },
  eventFilesLinks: string[] = [],
): string[] => item.filesLinks ?? eventFilesLinks;

export const resolveDocusignLinks = (source: DocumentLinksSource = {}): string[] => {
  const hasRowSignature = Boolean(source.linkedSignatureId || source.signatureStatus);
  const fromRow = uniqueLinks([
    ...(source.docusignFileLinks ?? []),
    ...(hasRowSignature && source.linkedSignatureFileLink ? [source.linkedSignatureFileLink] : []),
  ]);
  const fromEvent = resolveEventLevelDocusignFileLinks(source.event ?? null);

  return uniqueLinks([...fromEvent, ...fromRow]);
};

const resolveRecordStyleDocumentLinks = (
  filesLinks: string[],
  source: Pick<
    DocumentLinksSource,
    'docusignFileLinks' | 'linkedSignatureFileLink' | 'linkedSignatureId' | 'signatureStatus'
  >,
): { allLinks: string[]; docusignLinks: string[] } => {
  const signatureCandidates = uniqueLinks([
    ...(source.docusignFileLinks ?? []),
    ...(source.linkedSignatureFileLink ? [source.linkedSignatureFileLink] : []),
  ]);
  const docusignCandidates = uniqueLinks([
    ...signatureCandidates,
    ...resolveDisplayedDocusignFileLinks(filesLinks, signatureCandidates),
  ]);

  return resolveTransactionRowDocumentLinks(filesLinks, {
    ...source,
    docusignFileLinks: docusignCandidates,
  });
};

export const resolveEventLevelDocumentLinks = (
  event: NonNullable<DocumentLinksSource['event']> & PoolPlanEventSource,
): { allLinks: string[]; docusignLinks: string[] } => {
  if (event.type === EventTypeBackend.SAFE) {
    const docusignLinks = uniqueLinks([
      ...(event.docusignFileLinks ?? []),
      ...(event.signatureSummary?.signatureStatus && event.signatureSummary?.linkedSignatureFileLink
        ? [event.signatureSummary.linkedSignatureFileLink]
        : []),
    ]);
    const manualLinks = (event.filesLinks ?? []).filter(
      (link) => !isDocusignFileLink(link, docusignLinks),
    );

    return {
      allLinks: uniqueLinks([...manualLinks, ...docusignLinks]),
      docusignLinks,
    };
  }

  if (event.type === EventTypeBackend.SAFE_CONVERSION) {
    const filesLinks = uniqueLinks([
      ...(event.filesLinks ?? []),
      ...(event.conversion?.filesLinks ?? []),
    ]);

    return resolveRecordStyleDocumentLinks(filesLinks, {
      docusignFileLinks: event.docusignFileLinks,
      linkedSignatureFileLink: event.signatureSummary?.linkedSignatureFileLink,
      signatureStatus: event.signatureSummary?.signatureStatus,
    });
  }

  const recordFilesLinks = uniqueLinks([
    ...resolvePoolPlanRecordFileLinks(event),
    ...resolveSafeRecordFileLinks(event),
  ]);
  const eventWithMergedFiles =
    recordFilesLinks.length > 0 ? { ...event, filesLinks: recordFilesLinks } : event;

  const docusignLinks = uniqueLinks([
    ...resolveEventAllDocusignFileLinks(eventWithMergedFiles),
    ...(eventWithMergedFiles.docusignFileLinks ?? []),
  ]);

  const manualLinks = (eventWithMergedFiles.filesLinks ?? []).filter(
    (link) => !isDocusignFileLink(link, docusignLinks),
  );

  const allLinks = uniqueLinks([...manualLinks, ...docusignLinks]);

  return { allLinks, docusignLinks };
};

export const resolveTransactionRowDocumentLinks = (
  filesLinks: string[],
  source: DocumentLinksSource = {},
): { allLinks: string[]; docusignLinks: string[] } => {
  const { event } = source;

  if (event?.type && EVENT_TYPES_WITH_EVENT_LEVEL_SIGNATURE_ONLY.includes(event.type)) {
    const mergedDocusignFileLinks = uniqueLinks([
      ...(source.docusignFileLinks ?? []),
      ...(event.docusignFileLinks ?? []),
    ]);

    return resolveEventLevelDocumentLinks({
      ...event,
      docusignFileLinks: mergedDocusignFileLinks,
    } as NonNullable<typeof event> & PoolPlanEventSource);
  }

  const docusignLinks = resolveDocusignLinks(source);
  const manualLinks = filesLinks.filter((link) => !isDocusignFileLink(link, docusignLinks));
  const allLinks = uniqueLinks([...manualLinks, ...docusignLinks]);

  return { allLinks, docusignLinks };
};
